import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_CustomerCalendarRow, WB_ModelPredictorRow, WB_PowerMetricRelationshipRow,
    WB_PowerMetricRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbPowerMetric} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Customer_Calendar, esWB_Customer_Relationship, esWB_Model_Predictor,
    esWB_Power_Metric, esWB_Power_Metric_Relationship
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {ConstraintBasedOptimizerBOValidation} from "../PA.Simulation.BusinessObjects/ConstraintBasedOptimizerBOValidation";
import {ModelPowerMetricBOValidations} from "./ModelPowerMetricBO.Validations";
import { LeaseRegistry } from '../PA.Utils/LeaseRegistry';


export class ModelPowerMetricBO implements IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ModelPowerMetric: WB_PowerMetricRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Model PowerMetric'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_ModelPowerMetric = null;
    }

    get WB_Model_PowerMetric(): WB_PowerMetricRow {
        return this.drWB_ModelPowerMetric;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Model PowerMetric");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            self.LoadOptions = options || {};
            if('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if(this.dbContext == null) {
                let sError = "Unable to determine dbContext for New Model PowerMetric.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Power_Metric)
                .then((newModelPowerMetricRow: WB_PowerMetricRow)=>{
                    newModelPowerMetricRow.setDefaults();
                    self.drWB_ModelPowerMetric = newModelPowerMetricRow;
                    self.dbContext.add(esWB_Power_Metric, newModelPowerMetricRow);
                    self.Identity.setAll(self.WB_Model_PowerMetric.cif.WB_MODEL_POWERMETRIC_ID, self.WB_Model_PowerMetric.cif.POWERMETRIC_ID, self.WB_Model_PowerMetric.cif.POWERMETRIC_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Model_PowerMetric, 'WB_MODEL_POWERMETRIC_ID','POWERMETRIC_ID','POWERMETRIC_NAME');

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error)=>{

            if (self.bIsLoaded) {
                let sError = "Model Power Metric already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if(self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Model Power Metric .";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = ModelPowerMetricBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = ModelPowerMetricBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newModelPowerMetricRow: any = recordResolver.get(esWB_Power_Metric, anId);

                    if(_.isEmpty(newModelPowerMetricRow))
                    {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_ModelPowerMetric = newModelPowerMetricRow;
                    self.Identity.setAll(self.WB_Model_PowerMetric.cif.WB_MODEL_POWERMETRIC_ID, self.WB_Model_PowerMetric.cif.POWERMETRIC_ID, self.WB_Model_PowerMetric.cif.POWERMETRIC_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Model_PowerMetric, 'WB_MODEL_POWERMETRIC_ID','POWERMETRIC_ID','POWERMETRIC_NAME');
                })
                .then((newModelPowerMetricRow:any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err:any)=>{
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Model_PowerMetric.cif.POWERMETRIC_NAME + "(" + self.WB_Model_PowerMetric.cif.POWERMETRIC_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Model PowerMetric.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }
            ModelPowerMetricBOValidations.ValidateFields(self)
                .then((ignore) => {
                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbModelPowerMetric = <IwbPowerMetric>self.drWB_ModelPowerMetric;
        this.SaveProgressProvider.start("Saving " + wbModelPowerMetric.POWERMETRIC_NAME + "(" + wbModelPowerMetric.POWERMETRIC_ID + ")");

        return new Promise<IBusinessObject>((resolve, reject)=>{
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // We could search the tree if there is any record where store isn't set.

            if (self.dbContext == null) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save Model PowerMetric";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }
            let bDone = false
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return
                    }
                    return saveFunction(esWB_Power_Metric, self.drWB_ModelPowerMetric, {})
                        .then(() => {
                            var tasks: Promise<any>[] = []
                            tasks.push(saveFunction(esWB_Power_Metric_Relationship,
                                self.drWB_ModelPowerMetric.WB_PowerMetricRelationshipRows))
                        })
                        .then((ignored)=>{
                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(()=> {
                                    self._bIsSaved = true;
                                    LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                                })
                            } else {
                                self._bIsSaved = true;
                                LeaseRegistry.generateLeaseInvalidationByClass(LeaseRegistry.getLeaseClass(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Model)));
                            }
                            self.SaveProgressProvider.finish();
                            resolve(self);
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err);
                            reject(self);
                        })
                })


        })
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbModelPowerMetric = <IwbPowerMetric>self.drWB_ModelPowerMetric;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbModelPowerMetric.POWERMETRIC_NAME + "(" + wbModelPowerMetric.POWERMETRIC_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Power Metric

            // 2. If Valid, mark all the records for deletion.
            self.drWB_ModelPowerMetric.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //<editor-fold desc = "Power MetricR operations">

    addPowerMetricRel(aWB_PREDICTOR_ID: number, options: any): Promise<ModelPowerMetricBO> {

        const self = this;
        return new Promise<ModelPowerMetricBO>((resolve, error) => {
            // create new records
            let aModelPredictorRow: WB_ModelPredictorRow = undefined
            self.dbContext.find(esWB_Model_Predictor, aWB_PREDICTOR_ID)
                .then((aModelPredictorRow: WB_ModelPredictorRow) => {
                    aModelPredictorRow = aModelPredictorRow
                    return self.dbContext.createRecordWithKey(esWB_Power_Metric_Relationship)
                })
                .then((aNewRow: WB_PowerMetricRelationshipRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_MODEL_POWERMETRIC_ID = self.drWB_ModelPowerMetric.cif.WB_MODEL_POWERMETRIC_ID
                    aNewRow.cif.WB_MODEL_PREDICTOR_ID = aWB_PREDICTOR_ID
                    aNewRow.WB_ModelPredictorRow = aModelPredictorRow
                    self.dbContext.add(esWB_Power_Metric_Relationship, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })

        })

    }


    removePowerMetricRel(aWB_MODEL_POWERMETRICR_ID: number, options: any): Promise<ModelPowerMetricBO> {
        var self = this

        return new Promise<ModelPowerMetricBO>((resolve, error) => {

            _.forEach(self.drWB_ModelPowerMetric.WB_PowerMetricRelationshipRows, (aPowerMetricRelationshipRow: WB_PowerMetricRelationshipRow) => {
                if (aPowerMetricRelationshipRow.isActive && aPowerMetricRelationshipRow.cif.WB_MODEL_POWERMETRICR_ID == aWB_MODEL_POWERMETRICR_ID) {
                    aPowerMetricRelationshipRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    //</editor-fold>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const model_power_metric_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, model_power_metric_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Power_Metric,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( ModelPowerMetricBO ,"ModelPowerMetricBO", "Model Power Metric")
